<template>
  <div class="w-full flex flex-col">
    <div class="w-full text-center">{{subText}}</div>
    <div class="w-full flex flex-col h-full mt-4" v-for="link in youtubeLinks" :key="link.link">
      <div v-if="shownVideos[link.link]" class="aspect-video w-full max-h-[80dvh]">
        <iframe
          :src="getYouTubeEmbedUrl(link.link)"
          class="w-full h-full rounded"
          frameborder="0"
          allowfullscreen
          referrerpolicy="strict-origin-when-cross-origin"
        ></iframe>
      </div>
      <div v-else class="aspect-video w-full max-h-[80dvh] relative rounded overflow-hidden bg-black/10 flex flex-col items-center justify-center gap-3 p-4 text-center">
        <img :src="YouTubeLogo" alt="" class="w-16 h-16 opacity-70" />
        <button type="button" class="text-sm underline" @click="showVideo(link.link)">Show video (loads from YouTube)</button>
        <a :href="link.link" target="_blank" rel="noopener noreferrer" class="text-xs underline">Watch on YouTube</a>
      </div>
    </div>
    <div class="mt-10 flex flex-wrap justify-center gap-6" :style="cssVars">
      <a
          v-for="(link, index) in normalLinks"
          :key="link.link"
          v-motion
          :initial="{ opacity: 0, y: 20 }"
          :visible="{ opacity: 1, y: 0 }"
          :transition="{ duration: 0.5, ease: 'easeOut' }"
          class="block w-96 max-w-md hover:cursor-pointer hover:animate-pulse"
          :href="link.link"
          :target="isExternal(link.link) ? '_blank' : '_self'"
          rel="noopener"
      >
        <component :is="cardComponent" :colors="colors" :variant="index % 2 === 0 ? 1 : 2" class="p-6 h-full text-center">
        <div class="h-full rounded flex flex-col items-center justify-start gap-4" >
          <div class="mt-4 font-bold w-full flex items-center justify-center" style="color: var(--theme-text-heading);">
            <Link class="inline-block w-6 h-6 mr-2" :style="{color: colors.accent}" />
          </div>
          <div style="color: var(--theme-text-heading);">
            {{ link.label }}
          </div>
          <div v-if="linkPreviews[link.link]?.title" class="text-xs font-semibold" style="color: var(--theme-text-body);">
            {{ linkPreviews[link.link].title }}
          </div>
          <div v-if="linkPreviews[link.link]?.description" class="text-xs line-clamp-2" style="color: var(--theme-text-muted);">
            {{ linkPreviews[link.link].description }}
          </div>
          <img
            v-if="linkPreviews[link.link]?.image"
            :src="linkPreviews[link.link].image"
            class="max-h-16 w-full object-contain rounded"
            alt="Preview image"
          />

        </div>
        </component>
      </a>
    </div>
  </div>

</template>
<script setup>
import YouTubeLogo from '@/assets/youtube.svg';
import { ref, reactive, computed, onMounted } from 'vue';
import { useLinkPreview } from '@/composables/useLinkPreview';
import {Link} from "@iconoir/vue"
import {useThemedComponent} from "@/composables/useThemedComponent.js";

const cardComponent = useThemedComponent("card");
const props = defineProps({
  title: String,
  subText: String,
  links: Array,
  backgroundVariant: {type: Number, default: 1}, // Alternates background
  colors: {type: Object, required: true} // Use colors from the schema
});

const { previews: linkPreviews, fetchLinkPreview } = useLinkPreview();

const youtubeLinks = computed(() => props.links.filter(l => isYouTube(l.link)));
const normalLinks = computed(() => props.links.filter(l => !isYouTube(l.link)));

const isExternal = (url) => {
  if (!url) return false;
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return !url.startsWith('/') && (!origin || !url.startsWith(origin));
};

// Nothing is requested from YouTube until the visitor clicks, one video at a time.
const shownVideos = reactive({});
const showVideo = (url) => { shownVideos[url] = true; };

const isYouTube = (url) => {
  return /^(https?:\/\/)?(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/)/.test(url);
};

const getYouTubeEmbedUrl = (url) => {
  const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{6,20})/);
  return match ? `https://www.youtube-nocookie.com/embed/${match[1]}` : '';
};

// Compute CSS variables for dynamic background and card colors
const cssVars = computed(() => ({
  "--bg-light-variant-1": "#ffffff",
  "--bg-light-variant-2": props.colors.lightest,
  "--bg-dark-variant-1": props.colors.dark,
  "--bg-dark-variant-2": props.colors.darkest,
  "--card-light": props.colors.white,
  "--card-light-alt": props.colors.whitest,
  "--card-dark": props.colors.dark,
  "--card-dark-alt": props.colors.darkest,
  "--border-color-light": props.colors.accent,
  "--border-color-dark": props.colors.accentDark
}));

// Dynamically set background class using Tailwind dark mode
const computedBackgroundClass = computed(() =>
    props.backgroundVariant === 1
        ? "bg-[var(--bg-light-variant-1)] dark2:bg-[var(--bg-dark-variant-1)]"
        : "bg-[var(--bg-light-variant-2)] dark2:bg-[var(--bg-dark-variant-2)]"
);

onMounted(async () => {
  const previews = await Promise.all(
    props.links.map(async (link) => {
      let data = null;
      if (link.link) {
        data = await fetchLinkPreview(link.link);
        //return [link.link, preview];
      }
      return [link.link, data];
    })
  );
  console.log(previews);

  // for (const [url, preview] of previews) {
  //   if (preview) {
  //     linkPreviews.value[url] = preview;
  //   }
  // }
});
</script>

<style scoped>
/* Smooth background transitions */
.transition-colors {
  transition: background-color 0.3s ease-in-out;
}
</style>
